import { Box, Divider, Stack, Typography, useTheme } from "@mui/material";
import {
  Avatar,
  BadgeBase,
  CircularLoader,
  DeleteIcon,
  TemplateIcon,
} from "components";
import { useMemo } from "react";
import { type BillingTemplateListItem, PaymentTermOptions } from "shared";
import { extractStringFromHtml } from "utilz/helpers";

const ellipsisSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

/** Customer-specific: logo + "Customer • Site". General: template icon + "General". */
const TemplateCardHeader = ({
  template,
}: {
  template: BillingTemplateListItem;
}) => {
  const theme = useTheme();
  const isCustomerSpecific = template.type === "customer-specific";
  const subtitle = isCustomerSpecific
    ? [template.customer_name, template.site_name].filter(Boolean).join(" • ")
    : "General";

  return (
    <Stack gap="4px" sx={{ minWidth: 0 }}>
      <Stack direction="row" alignItems="center" gap="8px">
        {isCustomerSpecific ? (
          <Avatar
            sx={{ width: "24px", height: "24px", flexShrink: 0 }}
            fullname={template.customer_name ?? ""}
            url={template.logo ?? undefined}
          />
        ) : (
          <TemplateIcon
            sx={{ width: "20px", height: "20px", flexShrink: 0 }}
            htmlColor={theme.palette.lightBg.medium}
          />
        )}
        <Typography variant="subtitle1" sx={ellipsisSx}>
          {template.template_name}
        </Typography>
      </Stack>
      <Typography
        variant="body-small"
        color={theme.palette.lightBg.low}
        sx={ellipsisSx}
      >
        {subtitle}
      </Typography>
    </Stack>
  );
};

const TemplateCardBadge = ({ label }: { label: string }) => {
  const theme = useTheme();
  const indigo = theme.palette.primitive.system.indigo;

  return (
    <BadgeBase
      backgroundColor={indigo[50]}
      containerStyles={{
        width: "fit-content",
        height: "24px",
        padding: "4px 6px",
        borderRadius: "4px",
      }}
    >
      <Typography
        variant="body-xsmall"
        fontWeight="600"
        color={indigo[600]}
        sx={{ textTransform: "capitalize" }}
      >
        {label}
      </Typography>
    </BadgeBase>
  );
};

/** Design spec: 0px 0px 4px 0px #0000000F, 0px 0px 2px 0px #0000000A. */
const CARD_HOVER_SHADOW =
  "0px 0px 4px 0px rgba(0, 0, 0, 0.06), 0px 0px 2px 0px rgba(0, 0, 0, 0.04)";

export const BillingTemplateCard = ({
  template,
  isOpening = false,
  onClick,
  onDelete,
}: {
  template: BillingTemplateListItem;
  /** The full row is loading before the edit drawer opens. */
  isOpening?: boolean;
  onClick: () => void;
  onDelete: () => void;
}) => {
  const theme = useTheme();

  const paymentTerm = PaymentTermOptions.find(
    (item) => item.value === template.payment_term,
  )?.label;

  const descriptionText = useMemo(
    () => extractStringFromHtml(template.template_description || ""),
    [template.template_description],
  );

  return (
    <Stack
      sx={{
        width: "100%",
        height: "176px",
        bgcolor: theme.palette.darkBg.high,
        borderRadius: "8px",
        padding: "16px",
        border: `1px solid ${theme.palette.neutral["010"]}`,
        gap: "8px",
        cursor: isOpening ? "progress" : "pointer",
        transition: "box-shadow 0.2s ease-in-out",
        "& .template-delete-icon": {
          transition: "opacity 0.2s ease-in-out",
          opacity: 0,
        },
        "&:hover": {
          boxShadow: CARD_HOVER_SHADOW,
          "& .template-delete-icon": { opacity: 1 },
        },
      }}
      onClick={onClick}
    >
      <TemplateCardHeader template={template} />
      <Divider sx={{ borderColor: theme.palette.neutral["010"] }} />

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {descriptionText ? (
          <Typography
            variant="body-small"
            sx={{
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              lineClamp: 3,
              WebkitBoxOrient: "vertical",
            }}
          >
            {descriptionText}
          </Typography>
        ) : (
          <Typography
            variant="body-small"
            sx={{ color: theme.palette.lightBg.low, fontStyle: "italic" }}
          >
            No description
          </Typography>
        )}
      </Box>

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" gap="8px" alignItems="center">
          {template.invoice_type && (
            <TemplateCardBadge label={template.invoice_type} />
          )}
          {paymentTerm && <TemplateCardBadge label={paymentTerm} />}
        </Stack>
        {isOpening ? (
          <CircularLoader size="20px" />
        ) : (
          !template.meta?.is_default && (
            <DeleteIcon
              className="template-delete-icon"
              role="button"
              aria-label={`Delete ${template.template_name ?? "template"}`}
              onClick={(event) => {
                event.stopPropagation();
                onDelete();
              }}
              sx={{ cursor: "pointer" }}
            />
          )
        )}
      </Stack>
    </Stack>
  );
};
